import { Typography, Card } from '@ui/components';
import { Warning } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { ReactNode, FC } from 'react';

interface ErrorFallbackProps {
  componentStack?: Record<string, unknown> | string;
  type: 'route' | 'oidc';
  children?: ReactNode;
  eventId?: string;
  error?: Error;
}

export const ErrorFallback: FC<ErrorFallbackProps> = ({ componentStack, eventId, error, type }) => {
  const { t } = useTranslation();

  return (
    <main className="p-8 h-screen w-screen overflow-hidden bg-cover bg-[url('../images/background.svg')]">
      <Card className="size-full flex flex-col gap-8 items-center justify-center p-12">
        <div className="size-20 bg-surface-hover rounded-full flex items-center justify-center">
          <Warning className="size-12 text-border-disabled" />
        </div>

        <Typography className="text-weak" variant="h600">
          {t('ERROR_FALLBACK.TITLE')}
        </Typography>

        <Typography className="mt-2 text-weak" variant="h300">
          {t('ERROR_FALLBACK.DESCRIPTION')}
        </Typography>

        {(error || componentStack) && (
          <div className="overflow-auto bg-body p-4 rounded mt-8 min-w-2/3">
            <p className="font-semibold">
              {type === 'route' ? 'Route error' : 'OIDC error'} {eventId && `(eventId: ${eventId})`}
            </p>
            {error && (
              <details className="mt-2" open>
                <summary className="cursor-pointer font-medium">
                  <strong>Error:</strong> {error.message}
                </summary>
                <pre className="mt-2 overflow-auto text-sm">
                  <code>{error.stack}</code>
                </pre>
              </details>
            )}

            {componentStack && (
              <details className="mt-2" open>
                <summary className="cursor-pointer font-medium">
                  <strong>Component stack:</strong>
                </summary>
                <pre className="mt-2 overflow-auto text-sm">
                  <code>
                    {typeof componentStack === 'string'
                      ? componentStack
                      : JSON.stringify(componentStack, null, 2)}
                  </code>
                </pre>
              </details>
            )}
          </div>
        )}
      </Card>
    </main>
  );
};
